<template>
  <div
    id="settingsMenu"
    ref="settingsMenu"
    v-nuxt-click-away="clickAway"
    data-testid="header-menubutton"
  >
    <span class="sr-only"> Settings Menu </span>

    <DropdownMenuButtonV2
      v-if="isFeatureEnabled"
      to="/carrier-profile"
      data-testid="dropdown-menu-button-carrier-profile"
      @toggle-action="toggleMenu"
    />
    <DropdownMenuButton
      v-else
      data-testid="dropdown-menu-button"
      @toggle-action="toggleMenu"
    />

    <Tutorial
      v-if="isTooltipVisible && shouldShowTmsFeature"
      classes="tms-announcement-tutorial"
      header-icon="sync"
      arrow-classes="tms-announcement__arrow"
      @on-finish="handleTooltipClose"
      @on-close="handleTooltipClose"
    >
      <template #tutorial-header>Transportation Management System</template>
      <template #default>
        <div class="tw-flex tw-flex-col tw-gap-1">
          <span>Select your preferred system for managing your loads.</span>
          <span>We will use it to automatically sync loads and updates.</span>
          <span class="tw-hidden tw-md:tw-inline">Change this from your Profile setting.</span>
          <span class="tw-md:tw-hidden"
            >To change this, open the menu and go to Profile Settings.</span
          >
        </div>
      </template>
    </Tutorial>

    <slot name="menu-content">
      <ui-menu-anchor>
        <ui-menu v-model="open">
          <NuxtLink
            to="https://forms.gle/AobtohvG2NWyYVPa9"
            target="_blank"
            class="tw-no-underline"
            @click="trackEvent(eventNames.PROFILE_SEND_FEEDBACK_BUTTON_CLICKED)"
          >
            <DropdownItem
              icon="feedback"
              type="outlined"
            >
              <template #text>Send Feedback</template>
            </DropdownItem>
          </NuxtLink>
          <ui-menuitem-divider />
          <NuxtLink
            to="https://transport.acvauctions.com/legal"
            target="_blank"
            class="tw-no-underline"
            @click="trackEvent(eventNames.LEGAL_BUTTON_CLICKED)"
          >
            <DropdownItem
              icon="gavel"
              type="round"
            >
              <template #text>Legal</template>
            </DropdownItem>
          </NuxtLink>
          <ui-menuitem-divider />

          <DropdownItem
            icon="support_agent"
            type="filled"
            icon-position="tw-items-start"
            :has-arrow="false"
            @click="trackEvent(eventNames.CUSTOMER_SUPPORT_BUTTON_CLICKED)"
          >
            <template #text>
              <div class="tw-flex tw-flex-col tw-gap-4 tw-text-left">
                <div class="tw-mb-2">Customer Support</div>
                <div
                  v-for="(item, index) in computedCustomerSupportInformation"
                  :key="index"
                >
                  <div class="tw-mb-2">{{ item.name }}</div>
                  <div
                    v-if="item.amName"
                    class="tw-mb-2"
                  >
                    {{ item.amName }}
                  </div>
                  <a
                    class="tw-mb-2 tw-block tw-font-medium tw-text-secondary-base tw-no-underline"
                    :href="`tel:${item.rawPhoneNumber}`"
                    target="_blank"
                  >
                    {{ item.phoneNumber }}
                  </a>
                  <a
                    v-if="item.amEmail"
                    class="tw-mb-2 tw-block tw-font-medium tw-text-secondary-base tw-no-underline"
                    :href="`mailto:${item.amEmail}`"
                    target="_blank"
                  >
                    {{ item.amEmail }}
                  </a>
                  <a
                    class="tw-mb-2 tw-block tw-font-medium tw-text-secondary-base tw-no-underline"
                    :href="`mailto:${item.email}`"
                    target="_blank"
                  >
                    {{ item.email }}
                  </a>
                  <div class="tw-mb-2">{{ item.businessHours }}</div>
                </div>
              </div>
            </template>
          </DropdownItem>
          <ui-menuitem-divider />

          <DropdownItem
            icon="logout"
            type="filled"
            data-testid="header-logout"
            @click="handleLogout()"
          >
            <template #text>Logout</template>
          </DropdownItem>
        </ui-menu>
      </ui-menu-anchor>
    </slot>
  </div>
</template>

<script setup>
import { newFeatures } from '@/constants/newFeatures';
import { useFeatureFlagsStore } from '@/stores/useFeatureFlags';
import { featureFlagSlugs } from '@/constants/featureFlagSlugs';

const { logout } = useLogout();
const { trackEvent, eventNames } = useAcvAnalytics();
const open = ref(false);
const isFeatureEnabled = ref(false);

const featureFlagStore = useFeatureFlagsStore();
const isTmsEnabled = computed(() => featureFlagStore.getByFlagName(featureFlagSlugs.transportTms));

const { isFeatureNew, setFeatureIsNewToFalse } = useTooltipVisibility(
  newFeatures.preferredTmsSelectionNew,
);

const shouldShowTmsFeature = computed(() => {
  return isFeatureEnabled.value && isTmsEnabled.value;
});
const isTooltipVisible = ref(isFeatureNew);

const handleTooltipClose = () => {
  setFeatureIsNewToFalse();
  isTooltipVisible.value = false;
};

const toggleMenu = () => {
  open.value = !open.value;

  if (open.value) {
    trackEvent(eventNames.PROFILE_MENU_CLICKED);
  }
};

const clickAway = () => {
  open.value = false;
};

const handleLogout = () => {
  trackEvent(eventNames.LOGOUT_BUTTON_CLICKED);

  logout();
};

const computedCustomerSupportInformation = computed(() => useCustomerSupportInformation());

onMounted(async () => {
  const { isCarrierProfileEnabled } = await useIsCarrierProfileEnabled();
  isFeatureEnabled.value = isCarrierProfileEnabled.value;
});
</script>

<style lang="scss" scoped>
:deep() {
  .mdc-button {
    padding: 0.375rem 0.75rem;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
      border: 0;

      svg {
        @apply tw-text-black-500;
      }
    }

    svg {
      @apply tw-text-black-500;
    }
  }
  .mdc-deprecated-list {
    padding: 0;
  }
  .mdc-menu {
    .mdc-deprecated-list-divider {
      margin: 0;
    }
  }
  .mdc-deprecated-list-item {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 16px 20px;
    text-align: right;
    font-size: 16px;
    line-height: 1rem;
    height: auto;
    min-height: 48px;
    outline: none;
    transition: background-color 0.15s ease-in-out;
    .mdc-deprecated-list-item__graphic {
      margin-right: 0;
    }
    @apply tw-text-text-secondary;

    &:hover {
      background-color: #f3f3f3;
    }

    &:last-of-type {
      border: none;
    }
  }
  .mdc-menu {
    min-width: 300px;
  }
  .mdc-menu-surface {
    box-shadow:
      0px 0px 2px rgba(132, 132, 132, 0.14),
      0px 2px 2px rgba(132, 132, 132, 0.12),
      0px 1px 3px rgba(132, 132, 132, 0.2);
  }

  svg {
    @apply tw-text-text-secondary;
    // color: rgba(255, 255, 255, 0.8);
    font-size: 14px;

    &:hover {
      i,
      svg {
        color: rgba(255, 255, 255, 1);
      }
    }
  }
}
</style>

<style lang="scss">
.tutorial.tms-announcement-tutorial {
  position: fixed;
  bottom: 70px;
  right: 16px;
  width: calc(100vw - 32px);
  @apply tw-z-50;

  @screen tw-md {
    top: 70px;
    bottom: unset;
    right: 100px;
    width: 480px;
  }
}

.tms-announcement__arrow {
  bottom: -25px;
  right: 60px;
  transform: rotate(180deg);

  @screen tw-md {
    top: -25px;
    right: 80px;
    bottom: unset;
    transform: none;
  }
}
</style>
